import React, { useEffect, useState } from 'react';
import { Card, Descriptions, Switch, Typography, message } from 'antd';
import { useNarrowScreen } from '../hooks/useNarrowScreen';
import {
  fetchMe,
  fetchNotificationPreferences,
  updateNotificationPreferences,
  MeResponse,
} from '../api/kcc';

const { Title, Paragraph } = Typography;

const AccountSettingsPage: React.FC = () => {
  const narrow = useNarrowScreen();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [loadingMe, setLoadingMe] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingMe(true);
      try {
        const [meRes, pref] = await Promise.all([
          fetchMe(),
          fetchNotificationPreferences(),
        ]);
        if (!cancelled) {
          setMe(meRes);
          setEmailEnabled(!!pref.shipmentStatusEmailEnabled);
        }
      } catch (e: any) {
        if (!cancelled) {
          message.error(e?.response?.data?.message || 'Failed to load account');
        }
      } finally {
        if (!cancelled) setLoadingMe(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onToggle = async (checked: boolean) => {
    setSaving(true);
    try {
      const res = await updateNotificationPreferences({ shipmentStatusEmailEnabled: checked });
      setEmailEnabled(res.shipmentStatusEmailEnabled);
      message.success(checked ? 'Email notifications enabled' : 'Email notifications disabled');
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to update preference');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Title level={3}>Account</Title>
      <Paragraph type="secondary">
        Account details are read-only. Password resets are handled by operations support.
      </Paragraph>

      <Card loading={loadingMe} style={{ marginBottom: narrow ? 12 : 16 }} size={narrow ? 'small' : 'default'}>
        <Descriptions column={1} size="small">
          <Descriptions.Item label="Username">{me?.username || '—'}</Descriptions.Item>
          <Descriptions.Item label="Customer code">{me?.customerCode || '—'}</Descriptions.Item>
          <Descriptions.Item label="Email">{me?.email || '—'}</Descriptions.Item>
          <Descriptions.Item label="Role">{me?.role || '—'}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="Shipment status email" size={narrow ? 'small' : 'default'}>
        <Paragraph type="secondary">
          When enabled, you will receive an email when your shipment status changes.
        </Paragraph>
        <Switch
          checked={emailEnabled}
          loading={saving}
          onChange={onToggle}
          checkedChildren="On"
          unCheckedChildren="Off"
        />
      </Card>
    </div>
  );
};

export default AccountSettingsPage;
